<template>
  <v-card style="height: 100%; overflow: hidden">
    <ChartControl
      v-on:update-ranges="newData"
      :enabled="settingsEnabled"
      :loading="loading"
      :module="module"
    />
    <v-row no-gutters style="height: 75%">
      <e-chart
        v-if="opts.series.length > 0"
        :options="opts"
        :style="
          fakeData ? 'width: 100%; height: 300px' : 'width: 100%; height: 100%'
        "
        :autoresize="true"
        :theme="this.darkmode ? 'sensoronlinedark' : 'macarons'"
      />
    </v-row>
    <v-row no-gutters style="height: 5%">
      <span
        style="text-align: center; color: #999; width: 100%"
        v-if="resolution"
      >
        {{ displayResolution }}
      </span>
    </v-row>
  </v-card>
</template>

<script>
import ChartControl from "@/components/chart/ChartControl";
import { mapActions, mapState } from "vuex";
import "echarts/lib/component/title";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/dataZoom";
import "echarts/lib/chart/bar";
import "echarts/lib/coord/cartesian/Grid";

export default {
  name: "TrendChart",

  props: {
    settingsEnabled: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
    },
    module: {
      default: {},
    },
    tag: {
      default: null,
    },
    timerTick: {
      default: null,
    },
    fakeData: {
      default: false,
    },
  },

  data() {
    return {
      startDate: new Date(new Date().setHours(new Date().getHours() - 24)),
      endDate: new Date(),
      opts: {
        legend: {
          show: true,
          data: [],
        },
        grid: {
          top: 70,
          bottom: 50,
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
        },
        xAxis: {
          type: "time",
          splitLine: {
            show: true,
          },
          axisLine: {
            lineStyle: {
              width: 2,
            },
          },
        },
        yAxis: {
          type: "value",
        },
        dataZoom: [
          {
            show: true,
            type: "inside",
            throttle: 50,
          },
        ],
        series: [],
      },
      resolution: null,
    };
  },

  computed: {
    ...mapState("configuration", ["darkmode"]),

    displayResolution() {
      let tr = "";
      if (this.resolution > 24) {
        tr = this.$t("trend.res.week");
      } else if (this.resolution > 10) {
        tr = this.$t("trend.res.day");
      } else if (this.resolution > 1 && this.resolution < 10) {
        tr = this.$t("trend.res.fiveHours");
      } else if (this.resolution <= 1) {
        tr = this.$t("trend.res.hour");
      }

      return tr;
    },
  },

  methods: {
    ...mapActions("tagData", ["getTrendDataForTag"]),

    async updateData() {
      this.startDate = new Date(this.startDate - (this.endDate - new Date()));
      this.endDate = new Date();

      let data = await this.getTrendDataForTag({
        tagId: this.module.tag.deveui,
        tagKey: this.module.key,
        metrics: {
          from: this.startDate.toISOString(),
          to: this.endDate.toISOString(),
        },
      });

      if (data != undefined && data.legend > 0) {
        data.forEach((d) =>
          this.setData(this.module.tag.name + " - " + d.key, d.value, d.date)
        );

        if (data.length > 2)
          this.resolution = this.getDiff(data[0].date, data[1].date);
      }
    },

    getDiff(d1, d2) {
      if (!d1 || !d2) return null;
      let diff = (new Date(d2).getTime() - new Date(d1).getTime()) / 1000;
      diff /= 60 * 60;
      return Math.abs(Math.round(diff));
    },

    async newData(metrics) {
      if (metrics == undefined) return;

      this.startDate = metrics.from;
      this.endDate = metrics.to;

      let data = await this.getTrendDataForTag({
        tagId: this.module.tag.deveui,
        tagKey: this.module.key,
        metrics: {
          from: metrics.from,
          to: metrics.to,
        },
      });

      if (data != undefined && data.length > 0) {
        this.opts.series = [];
        this.opts.legend.data = [];
        data.forEach((d) =>
          this.setData(this.module.tag.name + " - " + d.key, d.value, d.date)
        );

        if (data.length > 2)
          this.resolution = this.getDiff(data[0].date, data[1].date);
      }
    },

    setData(name, value, date) {
      // Check if fieldname exist in series
      for (var i in this.opts.series) {
        if (this.opts.series[i].name == name) {
          if (!this.opts.legend.data.includes(name))
            this.opts.legend.data.push(name);

          this.opts.series[i].data.push({
            name: new Date(date).toString(),
            value: [date, +parseFloat(value).toFixed(2)],
          });
          this.opts.series[i].barMaxWidth = 50;
          return;
        }
      }

      this.opts.series.push({
        name: name,
        type: "bar",
        data: [],
        label: {
          show: true,
          position: "top",
          color: "#999",
        },
      });
    },

    setFakeData(name) {
      this.opts.series = [];
      this.opts.legend.data = [];
      var base = +new Date(1988, 9, 3);
      var oneDay = 24 * 3600 * 1000;
      var lastVal = Math.random(0, 20);
      for (var i = 1; i < 10; i++) {
        var now = new Date((base += oneDay));
        let d = [now.getFullYear(), now.getMonth() + 1, now.getDate()].join(
          "/"
        );
        let val = Math.round((Math.random() - 0.1) * 60 + lastVal);
        this.setData(name, val, d);
        lastVal = val;
      }
    },

    updateColors() {
      var color = this.module.meta.find((e) => e.key == "so_style_meta_color");
      if (color) {
        for (var i in this.opts.series) {
          this.opts.series[i].itemStyle = { color: color.content };
        }
      }
    },
  },

  async created() {
    if (!this.fakeData) {
      let storedTimeOffset = this.getModuleSettingsTimeOffset(
        this.module.dashboardModuleId
      );

      if (storedTimeOffset != undefined) {
        this.startDate = new Date(new Date() - storedTimeOffset);
        this.endDate = new Date();
      }

      let data = await this.getTrendDataForTag({
        tagId: this.module.tag.deveui,
        tagKey: this.module.key,
        metrics: {
          from: this.startDate.toISOString(),
          to: this.endDate.toISOString(),
        },
      });

      if (data != undefined && data.length > 0) {
        data.forEach((d) =>
          this.setData(this.module.tag.name + " - " + d.key, d.value, d.date)
        );

        if (data.length > 2)
          this.resolution = this.getDiff(data[0].date, data[1].date);
      }
    } else {
      let n = this.module.tag ? this.module.tag.name : "Sensor";
      let k = this.module.key ? this.module.key : "Key";
      this.setFakeData(n + " - " + k);
    }
    this.updateColors();
  },

  components: {
    ChartControl,
  },

  watch: {
    timerTick(v) {
      if (v % 12 == 0) {
        if (this.timeWithinHours(this.endDate, 1)) this.updateData();
      }
    },

    module() {
      if (this.fakeData && this.module.tag != undefined) {
        this.setFakeData(this.module.tag.name + " - " + this.module.key);
        this.updateColors();
      }
    },
  },
};
</script>